import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { FlexLayout } from '@Styled/utilities';
import SearchIcon from '@mui/icons-material/Search';
import {
	IconButton,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import SubmissionsPager from '../../Submissions/Components/SubmissionsPager';
import useStandalonePageLoader from '../useStandalonePageLoader';
import usePagedSubmissions, { PagedResponse } from '../../Submissions/Hooks/usePagedSubmissions';
import AttemptModal from './AttemptModal';
import TestSubmissionCard from './TestSubmissionCard';
import { TestSubmission } from './Submissions.types';

type LectureOption = { id: number; name: string };
type CourseLike = {
	id: number;
	tabs?: Array<{ sections?: Array<{ courses?: LectureOption[] }> }>;
	unsectioned_lectures?: LectureOption[];
};

type Props = { course: CourseLike };

/**
 * The manual-evaluation queue: attempts on essay tests across the whole
 * standalone course, graded and closed through the AttemptModal.
 */
const TestSubmissionsTable: React.FC<Props> = ({ course }) => {
	const { displaySnackbar } = useSnackbar();
	const { withLoading } = useStandalonePageLoader();
	const [searchInput, setSearchInput] = useState('');
	const [query, setQuery] = useState('');
	const [lectureId, setLectureId] = useState<number | ''>('');
	const [currentAttempt, setCurrentAttempt] = useState<TestSubmission>();

	const lectures = useMemo<LectureOption[]>(
		() =>
			(course.tabs || [])
				.flatMap((tab) => tab.sections || [])
				.flatMap((section) => section.courses || [])
				.concat(course.unsectioned_lectures || []),
		[course.tabs, course.unsectioned_lectures]
	);

	const { page, setPage, rows: attempts, meta, reload } = usePagedSubmissions<TestSubmission>(
		(activePage) =>
			withLoading(async () => {
				const response = await api.post('standalone-courses/test-submissions', {
					standalone_course_id: course.id,
					page: activePage,
					perPage: 10,
					query: query || undefined,
					...(lectureId ? { course_id: lectureId } : {}),
				});
				return response.data as PagedResponse<TestSubmission>;
			}),
		[course.id, query, lectureId],
		(error: any) => displaySnackbar('error', error?.response?.data?.message || 'Unable to load test submissions')
	);

	const applySearch = () => setQuery(searchInput.trim());

	const closeModal = () => {
		setCurrentAttempt(undefined);
		// a graded-and-closed attempt leaves the review queue — refetch
		reload();
	};

	return (
		<div>
			{currentAttempt && (
				<AttemptModal
					open
					submissionAttempt={currentAttempt}
					onClose={closeModal}
				/>
			)}
			<Controls>
				<TextField
					select
					size="small"
					label="Lecture"
					value={lectureId}
					sx={{ minWidth: '14rem' }}
					onChange={(e) => setLectureId(e.target.value === '' ? '' : Number(e.target.value))}
				>
					<MenuItem value="">All Lectures</MenuItem>
					{lectures.map((lecture) => (
						<MenuItem key={lecture.id} value={lecture.id}>
							{lecture.name}
						</MenuItem>
					))}
				</TextField>
				<FlexLayout alignItems="center">
					<TextField
						size="small"
						placeholder="Search students"
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') applySearch();
						}}
					/>
					<IconButton onClick={applySearch} aria-label="search">
						<SearchIcon />
					</IconButton>
				</FlexLayout>
			</Controls>
			{attempts.length === 0 ? (
				<Empty>No test submissions awaiting review</Empty>
			) : (
				attempts.map((attempt) => (
					<TestSubmissionCard
						key={attempt.id}
						test={attempt}
						actionLabel="Grade"
						onClick={(test) => setCurrentAttempt(test)}
					/>
				))
			)}
			<SubmissionsPager meta={meta} page={page} shown={attempts.length} onChange={setPage} />
		</div>
	);
};

const Controls = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 1rem;
	flex-wrap: wrap;
	margin-bottom: 1.5rem;
`;

const Empty = styled(Typography)`
	color: #a4a4a4;
	padding: 2rem 0;
	text-align: center;
`;

export default TestSubmissionsTable;
